import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { ArrowRight } from 'lucide-react';

export function Company() {
  const { theme } = useTheme();

  const timeline = [
    {
      year: "2023",
      title: "Victor Academy Launch",
      description: "Founded Victor Academy as a hub for human acceleration through education, innovation, and applied knowledge, combining AI tools with personalized mentorship"
    },
    {
      year: "2024",
      title: "Daedalus-1 Release",
      description: "Released Daedalus-1, our entry-level code reasoning model optimized for advanced code generation, debugging, and algorithmic reasoning"
    },
    {
      year: "2024",
      title: "Apollo Model Family",
      description: "Launched the Apollo Model Family (2B, 4B, 8B) - a suite of open, instruction-following models designed for consistent performance across applications"
    },
    {
      year: "2025",
      title: "Continued Innovation",
      description: "Expanding our research initiatives and developing next-generation AI systems for education, code reasoning, and human acceleration"
    }
  ];

  const values = [
    {
      title: "Innovation",
      description: "Pushing the boundaries of what's possible in AI"
    },
    {
      title: "Responsibility",
      description: "Ensuring AI development benefits humanity"
    },
    {
      title: "Collaboration",
      description: "Working together to solve complex challenges"
    },
    {
      title: "Transparency",
      description: "Open research and clear communication"
    }
  ];

  const learnMoreUrl = "#";

  return (
    <>
      <Background3D />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="max-w-[1440px] mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h1 className="text-6xl font-normal mb-8 leading-tight">
                  Advancing AI for <span className="text-gradient">humanity</span>
                </h1>
                <p className={`text-xl leading-relaxed mb-8 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Huxley Research is dedicated to developing safe and ethical AI systems 
                  that benefit humanity. Our mission is to ensure artificial intelligence 
                  remains aligned with human values and interests.
                </p>
                <div className="flex items-center space-x-6">
                  <a
                    href={learnMoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center px-6 py-3 text-lg text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-all duration-300 shadow-soft hover:shadow-lg"
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-radial from-primary-500/20 to-transparent opacity-70"></div>
                <img
                  src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80"
                  alt="Company Vision"
                  className="w-full h-[500px] object-cover rounded-3xl shadow-2xl hover-card"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className={`py-32 ${theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/50'}`}>
          <div className="max-w-[1440px] mx-auto px-6">
            <h2 className="text-4xl font-normal mb-16">Our Journey</h2>
            <div className="grid gap-8">
              {timeline.map((item, index) => (
                <div
                  key={index}
                  className={`relative p-8 rounded-2xl hover-card ${
                    theme === 'dark' ? 'bg-dark-300' : 'bg-white'
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div className="flex-shrink-0">
                      <div className={`text-5xl font-light ${
                        theme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                      }`}>
                        {item.year}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-normal mb-2">{item.title}</h3>
                      <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-32">
          <div className="max-w-[1440px] mx-auto px-6">
            <h2 className="text-4xl font-normal mb-16">Our Values</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  className={`p-8 rounded-2xl hover-card ${
                    theme === 'dark' ? 'bg-dark-300' : 'bg-white'
                  }`}
                >
                  <h3 className="text-2xl font-normal mb-4">{value.title}</h3>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}